import { useEffect, useMemo, useState } from "react";
import LinkCard from "../components/v2/LinkCard";
import PageTransition from "../components/v2/PageTransition";
import ShareNote from "../components/v2/ShareNote";
import SharePill from "../components/v2/SharePill";
import Wordmark from "../components/v2/Wordmark";
import "../components/v2/v2-question-surface.css";

function shareBody(note, link) {
  const trimmed = note.trim();
  // Keep share payloads limited to the introducer's own note and the gift link. Never append profile answers.
  return trimmed ? `${trimmed}\n\n${link}` : link;
}

const DONE_SCENE_MOBILE = 'url("/backgrounds/tgc-124/threshold-mobile-v1.png")';
const DONE_SCENE_DESKTOP = 'url("/backgrounds/tgc-124/threshold-desktop-v1.png")';

export default function Surface18({
  recipientName,
  recipientLink,
  giftCode = null,
  previewRecipientHref = null,
  direction = "forward",
  onMakeAnother,
}) {
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const recipient = recipientName || "them";
  const body = useMemo(() => shareBody(note, recipientLink), [note, recipientLink]);
  const smsHref = `sms:&body=${encodeURIComponent(body)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("A gift for you")}&body=${encodeURIComponent(body)}`;

  useEffect(() => {
    const savedBody = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      top: document.body.style.top,
      background: document.body.style.background,
    };
    const savedHtml = {
      overflow: document.documentElement.style.overflow,
      background: document.documentElement.style.background,
    };

    document.body.style.overflow = "auto";
    document.body.style.position = "static";
    document.body.style.width = "100%";
    document.body.style.top = "";
    document.body.style.background = "var(--tgc-world-cream)";
    document.documentElement.style.overflow = "auto";
    document.documentElement.style.background = "var(--tgc-world-cream)";

    return () => {
      document.body.style.overflow = savedBody.overflow;
      document.body.style.position = savedBody.position;
      document.body.style.width = savedBody.width;
      document.body.style.top = savedBody.top;
      document.body.style.background = savedBody.background;
      document.documentElement.style.overflow = savedHtml.overflow;
      document.documentElement.style.background = savedHtml.background;
    };
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(recipientLink);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <section className="tgc-v2-surface tgc-v2-surface--done" aria-labelledby="done-heading">
      <div
        className="tgc-v2-done-scene"
        style={{
          "--tgc-done-scene-mobile": DONE_SCENE_MOBILE,
          "--tgc-done-scene-desktop": DONE_SCENE_DESKTOP,
        }}
        aria-hidden="true"
      />
      <PageTransition direction={direction}>
        <div className="tgc-v2-done-shell">
          <Wordmark
            variant="stacked"
            markSrc="/brand/approved/tgc-conversation-loop-duotone.png"
            markClassName="tgc-v2-wordmark__sparkle--brand"
          />
          <h1 className="tgc-v2-done-heading" id="done-heading">All wrapped up.</h1>
          <p className="tgc-v2-done-body">
            {recipientName ? `${recipientName}'s conversation is ready.` : "Their conversation is ready."} Send it whenever you're ready -- no rush.
          </p>
          {giftCode && (
            <p className="tgc-v2-done-gift-code">
              Recovery code <strong>{giftCode}</strong>
            </p>
          )}
          <div className="tgc-v2-done-email-slot" aria-hidden="true" />
          <LinkCard url={recipientLink} href={previewRecipientHref} />
          <ShareNote value={note} onChange={setNote} />
          <div className="tgc-v2-done-ornament" aria-hidden="true">
            <span className="tgc-v2-done-ornament__line" />
            <img
              className="tgc-v2-done-ornament__sparkle tgc-v2-done-ornament__sparkle--brand"
              src="/brand/approved/tgc-conversation-loop-duotone.png"
              alt=""
            />
            <span className="tgc-v2-done-ornament__line" />
          </div>
          <div className="tgc-v2-share-stack" aria-live="polite">
            <SharePill ariaLabel={`Text the link to ${recipient}`} onClick={() => { window.location.href = smsHref; }}>
              Text it to {recipient}
            </SharePill>
            <SharePill ariaLabel={`Email the link to ${recipient}`} onClick={() => { window.location.href = emailHref; }}>
              Email it to {recipient}
            </SharePill>
            <SharePill ariaLabel="Copy the gift link" copied={copied} onClick={handleCopy}>
              Copy the link
            </SharePill>
          </div>
          <button className="tgc-v2-make-another" type="button" onClick={onMakeAnother}>
            Make another gift
          </button>
        </div>
      </PageTransition>
    </section>
  );
}
